import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ImageUp } from "lucide-react";
import type { PhotoUploadResponse, SearchContext } from "../../../shared/contracts.js";
import { api, errorMessage } from "../../api.js";
import { LensSearch, PhotoMatchList } from "../../components/PhotoSearch.js";
import { Button } from "../../components/ui/Button.js";
import { Callout } from "../../components/ui/States.js";
import { fmtInt } from "../../lib/format.js";
import { UPLOAD_MAX_FILE, prepareUpload } from "../../lib/photos.js";

/**
 * "Buscar con una foto del caso": la foto que recibió la aseguradora (del siniestro o
 * del vehículo) se compara con todas las fotos guardadas en Sentinel y, si el analista
 * lo pide, con Google Lens. Responde la pregunta clásica de fraude: ¿esta foto ya
 * existía en internet, y desde cuándo? La foto se procesa en memoria y no se guarda.
 */
export function CasePhoto({ context }: { context: () => SearchContext | null }) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: async (f: File): Promise<PhotoUploadResponse> => {
      const ctx = context();
      if (!ctx) throw new Error("Completa la finalidad de la búsqueda.");
      return api.photos.upload({ image: await prepareUpload(f), context: ctx });
    },
  });

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function pick(f: File | null): void {
    setError(null);
    upload.reset();
    if (f && f.size > UPLOAD_MAX_FILE) {
      setError("La foto pesa más de 25 MB.");
      return;
    }
    setFile(f);
    if (f) upload.mutate(f);
  }

  const r = upload.data;
  return (
    <details className="rounded-lg border border-border bg-surface p-3">
      <summary className="cursor-pointer font-semibold">
        <ImageUp className="mr-1.5 inline size-4 align-[-2px]" aria-hidden="true" />
        Buscar con una foto del caso
      </summary>
      <p className="my-2 text-[13px] text-muted">
        Sube la foto del siniestro o del vehículo que recibiste. Se busca la <strong>misma foto</strong> entre las fotos guardadas
        de los avisos (no autos parecidos) y, si lo pides, en internet. La foto no se guarda.
      </p>
      <label className="inline-flex items-center gap-2 text-[13px]">
        <span className="sr-only">Foto del caso</span>
        <input type="file" accept="image/jpeg,image/png,image/webp,image/heic" onChange={(e) => pick(e.target.files?.[0] ?? null)} />
      </label>
      {error && <Callout tone="danger" className="mt-2">{error}</Callout>}
      {upload.isPending && <p className="mt-2 text-[13px] text-muted" role="status">Comparando con las fotos guardadas…</p>}
      {upload.isError && (
        <Callout tone="danger" className="mt-2">
          {errorMessage(upload.error)}{" "}
          {file && <Button size="sm" variant="ghost" onClick={() => upload.mutate(file)}>Reintentar</Button>}
        </Callout>
      )}
      {r && (
        <div className="mt-3 space-y-3">
          {!r.comparable && (
            <Callout tone="warning" title="Foto poco comparable">
              Parece un pantallazo o una imagen casi lisa: la comparación por huella no es confiable.
            </Callout>
          )}
          {r.matches.length > 0 ? (
            <>
              <p className="text-[13px] font-medium">
                Aparece en {fmtInt(r.matches.length)} {r.matches.length === 1 ? "aviso" : "avisos"} guardados en Sentinel:
              </p>
              <PhotoMatchList matches={r.matches} basePlates={[]} preview={preview ?? undefined} />
            </>
          ) : r.comparable ? (
            <p className="text-[13px] text-muted">No aparece en las fotos guardadas de los avisos.</p>
          ) : null}
          <LensSearch key={r.uploadId} request={{ uploadId: r.uploadId }} label="Buscar también en internet" />
        </div>
      )}
    </details>
  );
}
